import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { SupplierRow } from '../../data/catalog.ts'
import { blankRow, EMPTY_ONLY, type FormulationRow } from '../../data/catalogueUi.ts'
import { useEngineState } from '../../club/ClubProvider.tsx'
import { Banner, PendingBanner } from '../../ui/components.tsx'
import { useWriteAccess } from '../../ui/hooks.ts'

/** The language for numbers and unit symbols (the two the app speaks). */
export function useLang(): 'fr' | 'en' {
  const { i18n } = useTranslation()
  return i18n.language.startsWith('en') ? 'en' : 'fr'
}

/** Cosmetic write gate (the server decides) plus the network: every catalogue write is an online command, so a form saves only when both hold. */
export function useCatalogueAccess() {
  const access = useWriteAccess('catalog.manage')
  const online = useEngineState().online !== false
  return { ...access, online, canSave: access.allowed && online }
}
export type Access = ReturnType<typeof useCatalogueAccess>
/** M15-A: the storeroom's acts (a receipt, a reversal, the places) are online intents under stock.manage: the same gate, the stock's own permission. */
export function useStockAccess() {
  const access = useWriteAccess('stock.manage')
  const online = useEngineState().online !== false
  return { ...access, online, canSave: access.allowed && online }
}

/** The server's verdict on the last action, where the user is looking. `id` prefixes the test ids (`<id>-error`, `<id>-ok`). */
export function Feedback({ id, error, ok, pending }: { id: string; error?: string | null; ok?: string | null; pending?: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => { if (error || ok || pending) ref.current?.scrollIntoView?.({ block: 'nearest' }) }, [error, ok, pending])
  if (!error && !ok && !pending) return null
  return (
    <div ref={ref} className="flex flex-col gap-2">
      {pending && <PendingBanner testId={`${id}-pending`} />}
      {error && <Banner tone="error"><span data-testid={`${id}-error`}>{error}</span></Banner>}
      {ok && <Banner tone="ok"><span data-testid={`${id}-ok`}>{ok}</span></Banner>}
    </div>
  )
}

/** Why Save is still grey: the checks the server will make again, said early (a field that is merely empty needs no message). */
export function Problems({ codes }: { codes: readonly string[] }) {
  const { t } = useTranslation()
  const shown = codes.filter((c) => !EMPTY_ONLY.has(c))
  if (shown.length === 0) return null
  return (
    <Banner tone="warn" testId="catalogue-problems">
      <p className="font-semibold">{t('catalogue.toFix')}</p>
      <ul className="ml-5 list-disc">{shown.map((c) => <li key={c} data-code={c}>{t(`catalogue.err.${c}`)}</li>)}</ul>
    </Banner>
  )
}

/** The supplier of a product or a batch: none, or one of the active suppliers (the one already chosen stays listed even if it was archived since). */
export function SupplierSelect({ id, value, onChange, suppliers, testId }: { id: string; value: string; onChange: (v: string) => void; suppliers: readonly SupplierRow[]; testId?: string }) {
  const { t } = useTranslation()
  const options = suppliers.filter((s) => !s.archived_at || s.id === value).sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }))
  return (
    <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      <option value="">{t('catalogue.form.noSupplier')}</option>
      {options.map((s) => <option key={s.id} value={s.id}>{s.name}{s.archived_at ? ` (${t('catalogue.archived')})` : ''}</option>)}
    </select>
  )
}

/** Formulation as editable name / value rows ("N" / "20"). Blank rows are dropped when the version is built. */
export function FormulationEditor({ rows, onChange }: { rows: FormulationRow[]; onChange: (rows: FormulationRow[]) => void }) {
  const { t } = useTranslation()
  const patch = (i: number, p: Partial<FormulationRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...p } : r)))
  return (
    <fieldset className="flex flex-col gap-2" data-testid="formulation-editor">
      <legend className="text-sm font-semibold">{t('catalogue.form.formulation')}</legend>
      <p className="meta">{t('catalogue.form.formulationHelp')}</p>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2" data-testid="formulation-row">
          <input className="input !min-h-12 !px-3" maxLength={60} value={r.key} aria-label={t('catalogue.form.componentName', { n: i + 1 })} placeholder={t('catalogue.form.componentNameShort')}
            onChange={(e) => patch(i, { key: e.target.value })} data-testid="formulation-key" />
          <input className="input !min-h-12 !px-3" maxLength={200} value={r.value} aria-label={t('catalogue.form.componentValue', { n: i + 1 })} placeholder={t('catalogue.form.componentValueShort')}
            onChange={(e) => patch(i, { value: e.target.value })} data-testid="formulation-value" />
          <button type="button" className="btn !min-h-12 !min-w-12 !px-0" aria-label={t('catalogue.form.removeComponent', { n: i + 1 })} onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid="formulation-remove">
            <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
      ))}
      <button type="button" className="btn !min-h-11" onClick={() => onChange([...rows, blankRow()])} data-testid="formulation-add">{t('catalogue.form.addComponent')}</button>
    </fieldset>
  )
}

/** A chevron that turns when its row is open. */
export function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className={`mt-1 size-5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
  )
}
